/* @jsxRuntime automatic */
/* @jsxImportSource @superweb/css */

import { useRef, type ReactElement, type ReactNode } from "react";
import {
  ErrorBoundary as ErrorBoundaryBase,
  type ErrorBoundaryProps,
} from "react-error-boundary";

import { v4 as uuidv4 } from "uuid";

import { HttpError, QueryErrorResetBoundary } from "@superweb/api";

import { FetchError } from "@fleet/api";

import { useErrorBoundaryOptions } from "./error-boundary-context";
import { Unexpected } from "./unexpected";

export type ErrorFallbackProps = {
  reset: () => void;
  error: Error;
  traceId?: string;
  clientDebugId?: string;
};

const enableHardResetOnError = Boolean(
  import.meta.env["FLEET_ENABLE_HARD_RESET_ON_CHUNK_ERROR"],
);

const testingOnly = import.meta.env["FLEET_ENV"] === "testing";

/** Temporary solution to enable hard reset on test production parks */
const isHardResetEnabledForTestProduction = () => {
  return (
    enableHardResetOnError &&
    (testingOnly ||
      [
        "79dbc5779ca14c5aabb3dab41ac93376",
        "5609c848b11c4eb99e874349f45f93d1",
      ].includes(new URLSearchParams(window.location.search).get("park_id")!))
  );
};

export const ErrorBoundary = ({
  children,
  fallback,
  onError,
  resetKeys,
}: {
  children?: ReactNode;
  fallback?: (props: ErrorFallbackProps) => ReactElement | null;
  onError?: (error: Error) => void;
  resetKeys?: ErrorBoundaryProps["resetKeys"];
}) => {
  const { logError } = useErrorBoundaryOptions();
  const currentErrorDebugId = useRef<string | null>(null);

  return (
    <QueryErrorResetBoundary>
      {({ reset: resetQueryErrorBoundary }) => {
        const generateDebugId = () => {
          currentErrorDebugId.current =
            "c_" + uuidv4().replace(/-/g, "").slice(2);
          return currentErrorDebugId.current;
        };

        return (
          <ErrorBoundaryBase
            onError={(error, { componentStack }) => {
              const traceId =
                error instanceof HttpError
                  ? error.headers.get("x-yatraceid") ?? undefined
                  : undefined;

              const debugId = currentErrorDebugId.current ?? generateDebugId();

              logError(
                {
                  message: error.message,
                  additional: {
                    componentStack: componentStack ?? undefined,
                    traceId,
                    clientDebugId: debugId,
                  },
                },
                error,
              );

              onError?.(error);
            }}
            onReset={() => {
              currentErrorDebugId.current = null;
              resetQueryErrorBoundary();
            }}
            resetKeys={resetKeys}
            fallbackRender={({
              resetErrorBoundary,
              error,
            }: {
              resetErrorBoundary: () => void;
              error: Error;
            }) => {
              const traceId =
                error instanceof HttpError
                  ? error.headers.get("x-yatraceid") ?? undefined
                  : undefined;

              const debugId = currentErrorDebugId.current ?? generateDebugId();

              const reset = isHardResetEnabledForTestProduction()
                ? () => {
                    if (
                      !(
                        error instanceof HttpError ||
                        error instanceof FetchError
                      )
                    ) {
                      window.location.reload();
                      return;
                    }
                    resetErrorBoundary();
                  }
                : resetErrorBoundary;

              if (fallback) {
                const Fallback = fallback;
                return (
                  <Fallback
                    reset={reset}
                    error={error}
                    traceId={traceId}
                    clientDebugId={debugId}
                  />
                );
              }

              return (
                <Unexpected
                  reset={reset}
                  traceId={traceId}
                  clientDebugId={debugId}
                />
              );
            }}
          >
            {children}
          </ErrorBoundaryBase>
        );
      }}
    </QueryErrorResetBoundary>
  );
};
